import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { concatMap, of } from 'rxjs';
import { catchError, debounceTime, filter, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import {
  BestMatchingProfileListItemResponse,
  CustomerDepartmentService,
  InquiryDateResponse,
  InquiryResponse,
} from '../../../api';
import { BaseTokenInterface } from '../../shared/interfaces/base-token-service.interface';
import { baseInquiryActions, baseOffersActions, baseOffersAndBookingsActions, baseSharedActions } from '../actions';
import { baseAuthSelectors, baseInquirySelectors } from '../selectors';
import { BaseInquiryService } from '../../modules/inquiry/_services/base-inquiry.service';
import { BaseCoreState } from '../reducers';
import { BaseInquiryStatus } from '../../modules/inquiry/_enums/base-inquiry.enum';
import { BaseFileExtension } from '../../shared/enums/base-available-file-type.enum';
import { BaseToastAlertClasses } from '../../shared/enums/base-toast-alert-classes.enum';
import { BaseShiftPlanService } from '../../modules/inquiry/_services/base-shift-plan.service';
import { BasePspInquiryOfferInterface } from '../../modules/inquiry/_interfaces/base-psp-inquiry-offer.interface';
import { BasePspInquiryOfferService } from '../../modules/inquiry/_services/base-psp-inquiry-offer.service';
import { BaseCustomerInquiryOfferInterface } from '../../modules/inquiry/_interfaces/base-cusomer-inquiry-offer.interface';
import { BaseCustomerInquiryOfferService } from '../../modules/inquiry/_services/base-customer-inquiry-offer.service';
import { ShiftPlanCalendarRangeInterface } from '../../modules/inquiry/_interfaces/base-shift-plan.interface';
import { BaseOffersAndBookingsListPayloadInterface } from '../../modules/offers-and-bookings/_interfaces/base-offers-and-bookings-list-payload.interface';
import { BaseOffersListPayloadInterface } from '../../modules/offers/_interfaces/base-offers-list-payload.interface';
import { BaseInquiryListPayloadInterface } from '../../modules/inquiry/_interfaces/base-inquiry-list-payload.interface';
import { InquiryResponseInterface } from '../../modules/inquiry/_interfaces/base-inquiry.interface';
import { BaseRolesService } from '../../core/services/base-roles.service';
import { BaseInquiryDocumentUploadService } from '../../modules/inquiry/_services/base-inquiry-document-upload.service';
import { baseGetMimeTypeByFileName } from '../../shared/helpers/base-get-mime-type-by-file-name';
import { BaseFileDownloadService } from '../../shared/services/base-file-download.service';

@Injectable()
export class BaseInquiryEffects {
  constructor(
    private actions$: Actions,
    private store: Store<BaseCoreState>,
    private baseInquiryService: BaseInquiryService,
    private departmentService: CustomerDepartmentService,
    private baseShiftViewPlanService: BaseShiftPlanService,
    private basePspInquiryOfferService: BasePspInquiryOfferService,
    private baseCustomerInquiryOfferService: BaseCustomerInquiryOfferService,
    private baseInquiryDocumentUploadService: BaseInquiryDocumentUploadService,
    private activatedRoute: ActivatedRoute,
    private roleService: BaseRolesService,
    private fileDownloadService: BaseFileDownloadService
  ) {}

  getInquiries$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseInquiryActions.getInquiries),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((user) => user.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        return this.baseInquiryService.getInquiries({ customer: user.organizationId, ...action.payload }).pipe(
          mergeMap((response) => [
            baseInquiryActions.loadInquiries({ payload: response }),
            baseInquiryActions.successLoadInquiries(),
          ]),
          catchError(() => of(baseInquiryActions.errorLoadInquiries()))
        );
      })
    );
  });

  getPspInquiries$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseInquiryActions.getPSPInquiries),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((user) => user.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        return this.baseInquiryService.getPSPInquiries({ psp: user.organizationId, ...action.payload }).pipe(
          mergeMap((response) => [
            baseInquiryActions.loadInquiries({ payload: response }),
            baseInquiryActions.successLoadInquiries(),
          ]),
          catchError(() => of(baseInquiryActions.errorLoadInquiries()))
        );
      })
    );
  });

  createInquiry$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseInquiryActions.createInquiry),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((user) => user.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        return this.baseInquiryService.createInquiry(user.organizationId, action.payload.data).pipe(
          tap(({ data }) => {
            if (action.payload.callback) {
              action.payload.callback(data.inquiryId);
            }
          }),
          mergeMap(() => [baseInquiryActions.successCreateInquiry()]),
          catchError(() => of(baseInquiryActions.errorCreateInquiry()))
        );
      })
    );
  });

  getDepartment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseInquiryActions.fetchDepartment),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((user) => user.data?.organizationId as number))
      ),
      switchMap(([action, userId]) => {
        return this.departmentService
          .getCustomerDepartmentShow({
            department: action.payload,
            customer: userId,
          })
          .pipe(
            mergeMap((response) => [
              baseInquiryActions.loadDepartment({ payload: response.data }),
              baseInquiryActions.loadDepartmentSuccess(),
            ])
          );
      }),
      catchError(() => of(baseInquiryActions.loadDepartmentError()))
    );
  });

  getPSPInquiryEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseInquiryActions.loadPSPInquiry),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((user) => user.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        return this.baseInquiryService
          .getPSPInquiry({
            psp: user.organizationId,
            inquiry: action.payload,
          })
          .pipe(
            mergeMap((response) => [
              baseInquiryActions.setPSPInquiry({
                payload: {
                  ...response,
                  weekdays: this.baseInquiryService.getInquiryWeekdays(response.dates as InquiryDateResponse[]),
                },
              }),
              baseInquiryActions.successPSPInquiry(),
              baseInquiryActions.loadInquiryBranches({ payload: { inquiry: action.payload } }),
            ]),
            catchError(() => of(baseInquiryActions.errorPSPInquiry()))
          );
      })
    );
  });

  getCandidateInquiry$ = createEffect(() =>
    this.actions$.pipe(
      ofType(baseInquiryActions.loadCustomerInquiry),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((user) => user.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        return this.baseInquiryService.getCustomerInquiry(user.organizationId, action.payload).pipe(
          mergeMap((response) => [
            baseInquiryActions.setCustomerInquiry({ payload: response }),
            baseInquiryActions.successCustomerInquiry(),
          ]),
          catchError(() => of(baseInquiryActions.errorCustomerInquiry()))
        );
      })
    )
  );

  getResponsibleUserList$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseInquiryActions.loadResponsibleUserList),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((user) => user.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        return this.baseInquiryService
          .responsibleUsersList({
            psp: user.organizationId,
            inquiry: action.payload,
          })
          .pipe(
            mergeMap((response: any) => [
              baseInquiryActions.setResponsibleUserList({ payload: response }),
              baseInquiryActions.successResponsibleUserList(),
            ]),
            catchError(() => of(baseInquiryActions.errorResponsibleUserList()))
          );
      })
    );
  });

  createInquiryOffer$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseInquiryActions.createInquiryOffer),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((user) => user.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        return this.baseInquiryService
          .createInquiryOffer({
            psp: user.organizationId,
            inquiry: action.payload.inquiry,
            body: action.payload.body,
          })
          .pipe(
            mergeMap(() => [
              baseInquiryActions.loadInquiryCandidatesList({
                payload: {
                  ...this.activatedRoute.snapshot.queryParams,
                  inquiry: action.payload.inquiry,
                },
              }),
              baseInquiryActions.loadPSPInquiry({ payload: action.payload.inquiry }),
            ])
          );
      })
    );
  });

  updateInquiryOffer$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseInquiryActions.updateInquiryOffer),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((user) => user.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        return this.baseInquiryService
          .updateInquiryOffer({
            psp: user.organizationId,
            offer: action.payload.offer,
            inquiry: action.payload.inquiry,
            body: action.payload.body,
          })
          .pipe(
            mergeMap(() => [
              baseInquiryActions.loadPspInquiryOffersList({
                payload: { ...this.activatedRoute.snapshot.queryParams, inquiry: action.payload.inquiry },
              }),
            ])
          );
      })
    );
  });

  getPSPCustomerQualificationPrice = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseInquiryActions.loadPSPCustomerQualificationPrice),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((user) => user.data as BaseTokenInterface))
      ),
      filter(([action, user]) => !!user),
      switchMap(([action, user]) => {
        return this.baseInquiryService
          .pspCustomerQualificationPrice({
            psp: user.organizationId,
            customer: action.payload.customer,
            qualification: action.payload.qualification,
            location: action.payload.locationId,
          })
          .pipe(
            mergeMap((response: any) => [
              baseInquiryActions.setPSPCustomerQualificationPrice({ payload: response }),
              baseInquiryActions.successPSPCustomerQualificationPrice(),
            ]),
            catchError(() => of(baseInquiryActions.errorPSPCustomerQualificationPrice()))
          );
      })
    );
  });

  acceptOffer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(baseInquiryActions.acceptOffer),
      withLatestFrom(this.store.select(baseAuthSelectors.getUserInfo).pipe(map((user) => user.data?.organizationId))),
      switchMap(([action, customer]) => {
        return this.baseInquiryService
          .acceptOffer(action.payload.offer, customer as number, action.payload.inquiry, {
            offerPositions: action.payload.offerPositions,
          })
          .pipe(
            mergeMap(() => [
              baseInquiryActions.acceptOfferSuccessful(),
              baseInquiryActions.loadCustomerInquiryOffersList({
                payload: { inquiry: action.payload.inquiry, ...this.activatedRoute.snapshot.queryParams },
              }),
              baseInquiryActions.loadCustomerInquiry({ payload: action.payload.inquiry }),
            ])
          );
      })
    )
  );

  downloadPspCustomerContractTemplateFile$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseInquiryActions.downloadPspCustomerContractTemplateFile),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((user) => user.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        return this.fileDownloadService
          .downloadFile(
            this.baseInquiryService.getPspCustomerContractTemplateFile({
              psp: user.organizationId,
              customer: action.payload,
            }),
            { documentType: BaseFileExtension.pdf }
          )
          .pipe(
            map(() => baseInquiryActions.downloadPspCustomerContractTemplateFileSuccess()),
            catchError(() => of(baseInquiryActions.downloadPspCustomerContractTemplateFileError))
          );
      })
    );
  });

  downloadCustomerContractDocument$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseInquiryActions.downloadCustomerContractDocument),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((user) => user.data as BaseTokenInterface))
      ),
      switchMap(([_action, user]) => {
        return this.fileDownloadService
          .downloadFile(this.baseInquiryService.getContractDocument(user.organizationId), {
            documentType: BaseFileExtension.pdf,
          })
          .pipe(
            map(() => baseInquiryActions.downloadCustomerContractDocumentSuccess()),
            catchError(() => of(baseInquiryActions.downloadCustomerContractDocumentError()))
          );
      })
    );
  });

  downloadCustomerInquiryOfferDocument$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseInquiryActions.downloadInquiryOfferDocument),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((user) => user.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        const { inquiry, offer, documentUuid, openInModal, documentType } = action.payload;

        return this.fileDownloadService
          .downloadFile(
            this.baseInquiryService.getCustomerInquiryOfferDocumentFileDownload({
              customer: user.organizationId,
              inquiry,
              offer,
              document: documentUuid,
            }),
            {
              isPreview: openInModal,
              documentType,
            }
          )
          .pipe(
            !openInModal
              ? map(() => baseInquiryActions.downloadInquiryOfferDocumentSuccess())
              : mergeMap((document) => [
                  baseInquiryActions.setDownloadInquiryOfferDocumentFile({
                    payload: { document: document as Blob },
                  }),
                  baseInquiryActions.downloadInquiryOfferDocumentSuccess(),
                ]),
            catchError(() => of(baseInquiryActions.downloadInquiryOfferDocumentError()))
          );
      })
    );
  });

  declineOfferCustomer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(baseInquiryActions.declineOfferCustomer),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((user) => user.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        const { inquiry, offer, body } = action.payload;

        return this.baseInquiryService
          .declineOfferCustomer({ customer: user.organizationId, offer, inquiry, body })
          .pipe(
            debounceTime(500),
            mergeMap((_) => {
              return action.payload.isCustomerOffersList
                ? [
                    baseOffersActions.loadOffersList({
                      payload: this.activatedRoute.snapshot.queryParams as BaseOffersListPayloadInterface,
                    }),
                    baseInquiryActions.declineOfferCustomerSuccess(),
                  ]
                : [
                    baseInquiryActions.loadCustomerInquiryOffersList({
                      payload: { ...this.activatedRoute.snapshot.queryParams, inquiry },
                    }),
                    baseInquiryActions.loadCustomerInquiry({ payload: inquiry }),
                    baseInquiryActions.declineOfferCustomerSuccess(),
                  ];
            }),
            catchError(() => of(baseInquiryActions.declineOfferCustomerError()))
          );
      })
    )
  );

  /**
   * PSP declines an inquiry. The list is refreshed from here (the established pattern in this file),
   * so the component never refetches. No toast on failure — the global HTTP error interceptor
   * already surfaces one, including the per-field messages from a 400.
   */
  declineInquiry$ = createEffect(() =>
    this.actions$.pipe(
      ofType(baseInquiryActions.declineInquiry),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((user) => user.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        const { inquiry, body } = action.payload;

        return this.baseInquiryService.declineInquiry({ psp: user.organizationId, inquiry, body }).pipe(
          mergeMap(() => [
            baseInquiryActions.declineInquirySuccess(),
            baseInquiryActions.getPSPInquiries({
              payload: this.activatedRoute.snapshot.queryParams as BaseInquiryListPayloadInterface,
            }),
          ]),
          catchError(() => of(baseInquiryActions.declineInquiryError()))
        );
      })
    )
  );

  revokeOffer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(baseInquiryActions.revokeOffer),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((user) => user.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        return this.baseInquiryService
          .revokeOffer(user.organizationId, action.payload.inquiry, action.payload.offer, action.payload.body)
          .pipe(
            debounceTime(500),
            mergeMap((_) => {
              const queryParams = this.activatedRoute.snapshot.queryParams;

              return action.payload.isOffersAndBookings
                ? [
                    baseInquiryActions.revokeOfferSuccess(),
                    baseOffersAndBookingsActions.loadOfferOverlapsList({
                      payload: queryParams as BaseOffersAndBookingsListPayloadInterface,
                    }),
                  ]
                : [
                    baseInquiryActions.revokeOfferSuccess(),
                    baseInquiryActions.loadPspInquiryOffersList({
                      payload: { ...queryParams, inquiry: action.payload.inquiry },
                    }),
                    baseInquiryActions.loadPSPInquiry({ payload: action.payload.inquiry }),
                  ];
            }),
            catchError(() => of(baseInquiryActions.revokeOfferError()))
          );
      })
    )
  );

  updateInquiryStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(baseInquiryActions.updateInquiryStatus),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((user) => user.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        return this.baseInquiryService
          .updateInquiryStatus(
            action.payload.inquiry,
            user.organizationId,
            action.payload.status,
            action.payload.closedNote
          )
          .pipe(
            tap(() => {
              if (action.payload.callback) {
                action.payload.callback();
              }
            }),
            mergeMap((_) => {
              return action.payload.loadUnpublishedInquiries || action.payload.isDashboard
                ? [
                    baseInquiryActions.updateInquiryStatusSuccess(),
                    baseSharedActions.addSystemAlert({
                      payload: {
                        body: 'successMessage.changesSaved',
                        class: BaseToastAlertClasses.success,
                      },
                    }),
                    baseInquiryActions.getInquiries({
                      payload: action.payload.loadUnpublishedInquiries
                        ? {
                            ...this.activatedRoute.snapshot.queryParams,
                            statuses: [BaseInquiryStatus.unpublished.toString()],
                          }
                        : {
                            page: 1,
                            size: 10,
                            statuses: [BaseInquiryStatus.unpublished.toString()],
                          },
                    }),
                  ]
                : [baseInquiryActions.updateInquiryStatusSuccess()];
            }),
            catchError(() => of(baseInquiryActions.updateInquiryStatusError()))
          );
      })
    )
  );

  updateCustomerInquiry$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseInquiryActions.updateCustomerInquiry),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((user) => user.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        return this.baseInquiryService
          .updateCustomerInquiry({
            customer: user.organizationId,
            inquiry: action.payload.inquiry.toString(),
            body: action.payload.body,
          })
          .pipe(
            tap(() => {
              if (action.payload.callback) {
                action.payload.callback();
              }
            }),
            mergeMap(() => [
              baseInquiryActions.loadCustomerInquiry({ payload: action.payload.inquiry }),
              baseInquiryActions.updateCustomerInquirySuccess(),
            ]),
            catchError(() => {
              return of(baseInquiryActions.updateCustomerInquiryError());
            })
          );
      })
    );
  });

  loadInquiryBranches$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseInquiryActions.loadInquiryBranches),
      withLatestFrom(this.store.select(baseAuthSelectors.getUserInfo)),
      switchMap(([action, user]) => {
        return this.baseInquiryService.getPspInquiryBranchList(user.data!.organizationId, action.payload.inquiry).pipe(
          mergeMap((response) => [
            baseInquiryActions.setInquiryBranches({ payload: response }),
            baseInquiryActions.successLoadInquiryBranches(),
          ]),
          catchError(() => of(baseInquiryActions.errorLoadInquiryBranches()))
        );
      })
    );
  });

  loadInquiryCandidatesList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(baseInquiryActions.loadInquiryCandidatesList),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((state) => state.data as BaseTokenInterface))
      ),
      concatMap(([action, user]) => {
        return this.baseInquiryService
          .getBestCandidatesByInquiry({
            ...action.payload,
            psp: user.organizationId,
          })
          .pipe(
            mergeMap((response) => {
              const notOfferedCandidates = (response.data as BestMatchingProfileListItemResponse[]).filter(
                (candidate) => !candidate.hasActiveOffers
              );

              return [
                baseInquiryActions.setInitialInquiryCandidatesList({
                  payload: {
                    data: notOfferedCandidates,
                    pagination: response.meta,
                  },
                }),
                baseInquiryActions.setInquiryCandidatesListSuccess(),
              ];
            }),
            catchError(() => of(baseInquiryActions.setInquiryCandidatesListError()))
          );
      })
    )
  );

  shiftPlanCreateData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(baseInquiryActions.shiftPlanCreateData),
      mergeMap((action) => {
        const shiftPlanRangeDataPayload = this.baseShiftViewPlanService.transformFormDataToShiftPlanViewData(
          action.payload
        );
        const shiftPlanCalendarList = this.baseShiftViewPlanService.generateInquiryShiftViewItems(
          shiftPlanRangeDataPayload ? [shiftPlanRangeDataPayload] : []
        );

        return [
          baseInquiryActions.setInquiryShiftViewRangeList({
            payload: shiftPlanRangeDataPayload ? [shiftPlanRangeDataPayload] : [],
          }),
          baseInquiryActions.setShiftPlanInquiryList({
            payload: {
              list: shiftPlanCalendarList,
              formData: this.baseShiftViewPlanService.prepareStaffRequestData(action.payload),
            },
          }),
        ];
      })
    )
  );

  openInquiryInEditMode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(baseInquiryActions.openInquiryInEditMode),
      mergeMap((action) => {
        const shiftPlanRangeDataPayload: ShiftPlanCalendarRangeInterface[] =
          this.baseShiftViewPlanService.prepareDataForShiftPlanView(action.payload.inquiry);

        const shiftPlanCalendarList =
          this.baseShiftViewPlanService.generateInquiryShiftViewItems(shiftPlanRangeDataPayload);

        const additionalActions =
          !!action.payload.inquiry.id && !!action.payload.inquiry.inquiryDocument
            ? [
                baseInquiryActions.loadInquiryDocument({
                  payload: {
                    inquiry: action.payload.inquiry.id as number,
                    document: action.payload.inquiry.inquiryDocument.id,
                  },
                }),
              ]
            : !action.payload.inquiry.id && !!action.payload.copiedInquiryId && !!action.payload.inquiry.inquiryDocument
            ? [
                baseInquiryActions.copyInquiryDocumentInfoToUpload({
                  payload: {
                    inquiry: action.payload.copiedInquiryId,
                    document: action.payload.inquiry.inquiryDocument.id,
                    documentName: action.payload.inquiry.inquiryDocument.name as string,
                  },
                }),
              ]
            : [];

        return [
          baseInquiryActions.setInquiryShiftViewRangeList({
            payload: shiftPlanRangeDataPayload,
          }),
          baseInquiryActions.setShiftPlanInquiryList({
            payload: {
              list: shiftPlanCalendarList,
              formData: this.baseShiftViewPlanService.convertToInquiryRequestData(action.payload.inquiry),
            },
          }),
          ...additionalActions,
        ];
      })
    )
  );

  shiftPlanUpdateData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(baseInquiryActions.shiftPlanUpdateData),
      withLatestFrom(this.store.select(baseInquirySelectors.getShiftPlanRangeList).pipe(filter((data) => !!data))),
      mergeMap(([action, shiftPlanRangeList]) => {
        const newShiftPlanRangeItem = this.baseShiftViewPlanService.transformFormDataToShiftPlanViewData(
          action.payload.data
        );
        const updatedShiftPlanRangeList: ShiftPlanCalendarRangeInterface[] = action.payload.rangeId
          ? shiftPlanRangeList.filter((item) => item.id !== action.payload.rangeId)
          : shiftPlanRangeList;
        const updatedListWithNewItem: ShiftPlanCalendarRangeInterface[] = newShiftPlanRangeItem
          ? [...updatedShiftPlanRangeList, newShiftPlanRangeItem].sort(
              (a, b) => new Date(a.dates[0].dateString).getTime() - new Date(b.dates[0].dateString).getTime()
            )
          : updatedShiftPlanRangeList;

        return [
          baseInquiryActions.setInquiryShiftViewRangeList({ payload: updatedListWithNewItem }),
          baseInquiryActions.updateShiftPlanInquiryList({
            payload: this.baseShiftViewPlanService.generateInquiryShiftViewItems(updatedListWithNewItem),
          }),
        ];
      })
    )
  );

  removeShiftPlanRange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(baseInquiryActions.removeShiftPlanRange),
      withLatestFrom(this.store.select(baseInquirySelectors.getShiftPlanRangeList).pipe(filter((data) => !!data))),
      mergeMap(([action, shiftPlanRangeList]) => {
        const updatedShiftPlanRangeList: ShiftPlanCalendarRangeInterface[] = shiftPlanRangeList.filter(
          (item) => item.id !== action.payload
        );

        return [
          baseInquiryActions.setInquiryShiftViewRangeList({ payload: updatedShiftPlanRangeList }),
          baseInquiryActions.updateShiftPlanInquiryList({
            payload: this.baseShiftViewPlanService.generateInquiryShiftViewItems(updatedShiftPlanRangeList),
          }),
        ];
      })
    )
  );

  loadPspInquiryOffersList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(baseInquiryActions.loadPspInquiryOffersList),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((state) => state.data as BaseTokenInterface)),
        this.store.select(baseInquirySelectors.getPSPInquiry)
      ),
      switchMap(([action, user, inquiry]) => {
        return this.baseInquiryService
          .getOffersByInquiry({
            ...action.payload,
            psp: user.organizationId,
          })
          .pipe(
            mergeMap((response) => {
              const payload: BasePspInquiryOfferInterface[] =
                this.basePspInquiryOfferService.preparePspInquiryOfferBubblesList(
                  response.data,
                  inquiry.data as InquiryResponseInterface
                );
              return [
                action.payload.loadNextPage
                  ? baseInquiryActions.addPspInquiryOffersListNextPage({ payload })
                  : baseInquiryActions.setPspInquiryOffersList({ payload }),
                baseInquiryActions.setPspInquiryOffersListPagination({ payload: response.meta }),
                baseInquiryActions.setPspInquiryOffersListSuccess(),
              ];
            }),
            catchError(() => of(baseInquiryActions.setPspInquiryOffersListError()))
          );
      })
    )
  );

  selectPspInquiryOffer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(baseInquiryActions.selectPspInquiryOffer),
      withLatestFrom(this.store.select(baseInquirySelectors.getPspInquiryOffers)),
      map(([action, offers]) => {
        return baseInquiryActions.setPspInquiryOffersList({
          payload: this.basePspInquiryOfferService.selectPspInquiryOffer(offers.data, action.payload.offerId),
        });
      })
    )
  );

  loadCustomerInquiryOffersList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(baseInquiryActions.loadCustomerInquiryOffersList),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((user) => user.data as BaseTokenInterface)),
        this.store.select(baseInquirySelectors.getCustomerInquiry)
      ),
      switchMap(([action, user, inquiry]) => {
        return this.baseInquiryService
          .getCustomerInquiryOffers({
            ...action.payload,
            customer: user.organizationId,
          })
          .pipe(
            mergeMap((response) => {
              const payload: BaseCustomerInquiryOfferInterface[] =
                this.baseCustomerInquiryOfferService.prepareCustomerInquiryOfferBubblesList(
                  response.data,
                  inquiry.data as InquiryResponse
                );
              return [
                action.payload.loadNextPage
                  ? baseInquiryActions.addCustomerInquiryOffersListNextPage({ payload })
                  : baseInquiryActions.setCustomerInquiryOffersList({ payload }),
                baseInquiryActions.setCustomerInquiryOffersFiltersAndPagination({
                  payload: { filters: response.filters, pagination: response.meta },
                }),
                baseInquiryActions.setCustomerInquiryOffersListSuccess(),
              ];
            }),
            catchError(() => of(baseInquiryActions.setCustomerInquiryOffersListError()))
          );
      })
    )
  );

  selectCustomerInquiryOffer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(baseInquiryActions.selectCustomerInquiryOffer),
      withLatestFrom(this.store.select(baseInquirySelectors.getCustomerInquiryOffers)),
      map(([action, offers]: any) => {
        return baseInquiryActions.setCustomerInquiryOffersList({
          payload: this.baseCustomerInquiryOfferService.selectCustomerInquiryOffer(offers.data, action.payload.offerId),
        });
      })
    )
  );

  selectCustomerOfferBubble$ = createEffect(() =>
    this.actions$.pipe(
      ofType(baseInquiryActions.selectCustomerOfferBubble),
      withLatestFrom(this.store.select(baseInquirySelectors.getCustomerInquiryOffers)),
      map(([action, offers]: any) => {
        return baseInquiryActions.setCustomerInquiryOffersList({
          payload: this.baseCustomerInquiryOfferService.updateCustomerOffersListWithSelectedBubble(
            offers.data,
            action.payload
          ),
        });
      })
    )
  );

  customerOfferFlexShiftsSelection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(baseInquiryActions.customerOfferFlexShiftsSelection),
      withLatestFrom(this.store.select(baseInquirySelectors.getCustomerInquiryOffers)),
      map(([action, offers]: any) => {
        return baseInquiryActions.setCustomerInquiryOffersList({
          payload: this.baseCustomerInquiryOfferService.selectAllFlexShifts(offers.data, action.payload.offerId),
        });
      })
    )
  );

  customerOfferFixedShiftsSelection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(baseInquiryActions.customerOfferFixedShiftsSelection),
      withLatestFrom(this.store.select(baseInquirySelectors.getCustomerInquiryOffers)),
      map(([action, offers]: any) => {
        return baseInquiryActions.setCustomerInquiryOffersList({
          payload: this.baseCustomerInquiryOfferService.selectAllFixedShiftsByType(
            offers.data,
            action.payload.offerId,
            action.payload.shiftType
          ),
        });
      })
    )
  );

  customerOfferAllShiftsSelection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(baseInquiryActions.customerOfferAllShiftsSelection),
      withLatestFrom(this.store.select(baseInquirySelectors.getCustomerInquiryOffers)),
      map(([action, offers]: any) => {
        return baseInquiryActions.setCustomerInquiryOffersList({
          payload: this.baseCustomerInquiryOfferService.selectAllOfferedShifts(
            offers.data,
            action.payload.offerId,
            action.payload.isShiftsDeselectionMode
          ),
        });
      })
    )
  );

  loadSimilarInquiries$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseInquiryActions.loadSimilarInquiries),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((user) => user.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        return this.baseInquiryService
          .getSimilarInquiries({
            customer: user.organizationId,
            ...action.payload,
          })
          .pipe(
            mergeMap((response) => {
              return [
                baseInquiryActions.setSimilarInquiries({ payload: response }),
                baseInquiryActions.successSetSimilarInquiries(),
              ];
            }),
            catchError(() => of(baseInquiryActions.errorSetSimilarInquiries()))
          );
      })
    );
  });

  customerInquiryOfferPreselection$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseInquiryActions.customerInquiryOfferPreselection),
      withLatestFrom(this.store.select(baseAuthSelectors.getUserInfo)),
      switchMap(([action, userInfo]) => {
        const source$ = action.payload.preselect
          ? this.baseInquiryService.preselectCustomerInquiryOffer({
              customer: userInfo?.data?.organizationId as number,
              inquiry: action.payload.inquiry,
              offer: action.payload.offer,
            })
          : this.baseInquiryService.unselectCustomerInquiryOffer({
              customer: userInfo?.data?.organizationId as number,
              inquiry: action.payload.inquiry,
              offer: action.payload.offer,
            });

        return source$.pipe(
          mergeMap(() => {
            return action.payload.isCustomerOffersList
              ? [
                  baseInquiryActions.successCustomerInquiryOfferPreselection(),
                  baseOffersActions.loadOffersList({
                    payload: this.activatedRoute.snapshot.queryParams as BaseOffersListPayloadInterface,
                  }),
                ]
              : [
                  baseInquiryActions.successCustomerInquiryOfferPreselection(),
                  baseInquiryActions.loadCustomerInquiryOffersList({
                    payload: { ...this.activatedRoute.snapshot.queryParams, inquiry: action.payload.inquiry },
                  }),
                ];
          }),
          catchError(() => of(baseInquiryActions.errorCustomerInquiryOfferPreselection()))
        );
      })
    );
  });

  loadDefaultDeliveryTimeList$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseInquiryActions.loadDefaultDeliveryTimeList),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((user) => user.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        return this.baseInquiryService
          .getDefaultDeliveryTimeList({
            customer: user.organizationId,
            inquiry: action.payload,
          })
          .pipe(
            mergeMap((response) => {
              return [
                baseInquiryActions.setDefaultDeliveryTimeList({ payload: response }),
                baseInquiryActions.successDefaultDeliveryTimeList(),
              ];
            }),
            catchError(() => of(baseInquiryActions.errorDefaultDeliveryTimeList()))
          );
      })
    );
  });

  createCustomerInquiryDeliveryTimes$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(baseInquiryActions.createCustomerDeliveryTimeList),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((user) => user.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        return this.baseInquiryService
          .createCustomerInquiryDeliveryTimes({
            customer: user.organizationId,
            inquiry: action.payload.inquiryId,
            body: action.payload.body,
          })
          .pipe(
            tap(() => {
              if (action.payload.callback) {
                action.payload.callback();
              }
            }),
            map(() => baseInquiryActions.successCreateCustomerDeliveryTimeList()),
            catchError(() => of(baseInquiryActions.errorCreateCustomerDeliveryTimeList()))
          );
      })
    );
  });

  loadInquiryProviders$ = createEffect(() =>
    this.actions$.pipe(
      ofType(baseInquiryActions.loadInquiryProviders),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((user) => user.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        return this.baseInquiryService
          .getInquiryProviders({ customer: user.organizationId, inquiry: action.payload.inquiryId })
          .pipe(
            mergeMap((response) => [
              baseInquiryActions.setInquiryProviders({ payload: response }),
              baseInquiryActions.successInquiryProviders(),
            ]),
            catchError(() => of(baseInquiryActions.errorInquiryProviders()))
          );
      })
    )
  );

  sendInquiryProvidersImmediately$ = createEffect(() =>
    this.actions$.pipe(
      ofType(baseInquiryActions.sendInquiryProvidersImmediately),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((user) => user.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        return this.baseInquiryService
          .sendProvidersImmediately({
            ...action.payload,
            customer: user.organizationId,
          })
          .pipe(
            mergeMap(() => [
              baseInquiryActions.sendInquiryProvidersImmediatelySuccess(),
              baseInquiryActions.loadInquiryProviders({ payload: { inquiryId: action.payload.inquiry } }),
            ]),
            catchError(() => of(baseInquiryActions.sendInquiryProvidersImmediatelyError()))
          );
      })
    )
  );

  uploadInquiryDocumentFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(baseInquiryActions.uploadInquiryDocumentFile),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((user) => user.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        const { documentToUpload, requestData, createInquiryCallback, updateInquiryCallback, inquiryId } =
          action.payload;

        return this.baseInquiryService
          .uploadInquiryDocument({
            customer: user.organizationId,
            body: documentToUpload,
          })
          .pipe(
            tap(() => {
              this.baseInquiryDocumentUploadService.clearDocumentInfo();
            }),
            mergeMap((response) => [
              baseInquiryActions.updateInquiryDocument({
                payload: {
                  inquiry: inquiryId as number,
                  document: response.documentId as string,
                  body: { name: documentToUpload.name },
                },
              }),
              baseInquiryActions.uploadInquiryDocumentFileSuccess(),
              inquiryId
                ? baseInquiryActions.updateCustomerInquiry({
                    payload: {
                      inquiry: inquiryId,
                      body: {
                        ...requestData,
                        document: response.documentId as string,
                      },
                      callback: updateInquiryCallback,
                    },
                  })
                : baseInquiryActions.createInquiry({
                    payload: {
                      data: {
                        ...requestData,
                        document: response.documentId as string,
                      },
                      callback: createInquiryCallback,
                    },
                  }),
            ]),
            catchError(() => of(baseInquiryActions.uploadInquiryDocumentFileError()))
          );
      })
    )
  );

  downloadInquiryDocumentFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(baseInquiryActions.downloadInquiryDocumentFile),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((user) => user.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        const { inquiry, document, isPreview, documentType } = action.payload;

        const source$ = this.roleService.isPsp()
          ? this.baseInquiryService.downloadExistingPSPInquiryDocumentFile({
              psp: user.organizationId,
              inquiry,
              document,
            })
          : this.baseInquiryService.downloadExistingCustomerInquiryDocumentFile({
              customer: user.organizationId,
              inquiry,
              document,
            });

        return this.fileDownloadService
          .downloadFile(source$, {
            isPreview,
            documentType,
          })
          .pipe(
            !isPreview
              ? map(() => baseInquiryActions.successDownloadInquiryDocumentFile())
              : mergeMap((downloadedDocument) => [
                  baseInquiryActions.setDownloadedInquiryDocumentFile({
                    payload: { document: downloadedDocument as Blob },
                  }),
                  baseInquiryActions.successDownloadInquiryDocumentFile(),
                ]),
            catchError(() => of(baseInquiryActions.errorDownloadInquiryDocumentFile()))
          );
      })
    )
  );

  updateInquiryDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(baseInquiryActions.updateInquiryDocument),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((user) => user.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        return this.baseInquiryService
          .updateInquiryDocument({
            customer: user.organizationId,
            ...action.payload,
          })
          .pipe(
            map(() => baseInquiryActions.updateInquiryDocumentSuccess()),
            catchError(() => of(baseInquiryActions.updateInquiryDocumentError()))
          );
      })
    )
  );

  loadInquiryDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(baseInquiryActions.loadInquiryDocument),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((user) => user.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        return this.baseInquiryService
          .getInquiryDocument({
            customer: user.organizationId,
            ...action.payload,
          })
          .pipe(
            mergeMap((response) => [
              baseInquiryActions.setInquiryDocument({
                payload: response,
              }),
              baseInquiryActions.successLoadInquiryDocument(),
            ]),
            catchError(() => of(baseInquiryActions.errorLoadInquiryDocument()))
          );
      })
    )
  );

  deleteInquiryDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(baseInquiryActions.deleteInquiryDocument),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((user) => user.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        return this.baseInquiryService
          .deleteInquiryDocument({
            customer: user.organizationId,
            ...action.payload,
          })
          .pipe(
            mergeMap(() => [
              baseInquiryActions.deleteInquiryDocumentSuccess(),
              baseInquiryActions.clearDownloadedInquiryDocumentFile(),
            ]),
            catchError(() => of(baseInquiryActions.deleteInquiryDocumentError()))
          );
      })
    )
  );

  copyInquiryDocumentInfoToUpload$ = createEffect(() =>
    this.actions$.pipe(
      ofType(baseInquiryActions.copyInquiryDocumentInfoToUpload),
      withLatestFrom(
        this.store.select(baseAuthSelectors.getUserInfo).pipe(map((user) => user.data as BaseTokenInterface))
      ),
      switchMap(([action, user]) => {
        return this.baseInquiryService
          .downloadExistingCustomerInquiryDocumentFile({
            customer: user.organizationId,
            inquiry: action.payload.inquiry,
            document: action.payload.document,
          })
          .pipe(
            tap((response: any) => {
              this.baseInquiryDocumentUploadService.setDocumentInfo({
                file: new File([response.body], response.fileName, {
                  type: baseGetMimeTypeByFileName(response.fileName),
                }),
                name: action.payload.documentName,
              });
            }),
            map(() => baseInquiryActions.successCopyInquiryDocumentInfoToUpload()),
            catchError(() => of(baseInquiryActions.errorCopyInquiryDocumentInfoToUpload()))
          );
      })
    )
  );
}
